<template>
  <DxPopup
    :visible="showModal"
    :drag-enabled="false"
    :hide-on-outside-click="false"
    :show-title="true"
    :showCloseButton="false"
    :height="600"
    @shown="initialize"
    :title.sync="modalTitle"
  >
    <div id="popup-wrapper">
      <DxScrollView id="generate-so-scrollview" ref="scrollViewWidget">
        <div class="responsive-paddings">
          <form>
            <DxForm
              :form-data="model"
              :read-only="false"
              :show-colon-after-label="false"
              :show-validation-summary="false"
              :validation-group="'headerForm'"
              :label-location="formsConfig.labelPosition"
              :items="headerFormItems"
              :show-required-mark="false"
              ref="generate-so-header-form"
              @content-ready="onFormContentReady"
            >
              <!-- SelectBox Template  -->
            </DxForm>
          </form>
        </div>
        <div>
          <!--details grid -->
          <dx-data-grid
            v-if="loadingCompleted"
            class="dx-card wide-card generate-so-from-line-grid"
            :data-source="salesOrderDetailLines"
            :show-borders="false"
            :ref="'generate-so-grid'"
            :focused-row-enabled="false"
            :column-auto-width="true"
            :column-hiding-enabled="false"
            :selection="{ mode: 'single' }"
            :hover-state-enabled="true"
            :column-min-width="100"
            :height="340"
            :reshape-on-push="true"
            @initialized="generateSoInitialized"
            :column-fixing-enabled="true"
            :columns="generateSoDetailsColumns"
            validation-group="formsData"
            @content-ready="addGridNoResults($event)"
            :allow-column-reordering="false"
            :column-chooser="{ enabled: false }"
          >
            <!-- key-expr="Key" -->
            <dx-paging :page-size="50" />
            <dx-pager :show-page-size-selector="true" :show-info="true" />

            <DxSearchPanel :visible="true" :highlight-case-sensitive="true" />
            <DxEditing :allow-updating="true" mode="cell" />

            <template #textEditTemplate="{ data }">
              <div>
                <div>
                  <DxTextBox
                    :value="data.value"
                    @value-changed="onTextValueChanged($event, data)"
                    class="gc-grid-number-box-editor"
                    :ref="data.column.dataField + '-' + data.rowIndex"
                    :maxLength="data.column.dataField == 'OrderNo' ? 20 : 100"
                  />
                </div>
              </div>
            </template>
            <template #productEditTemplate="{ data: cellInfo }">
              <div class="gc-auto-cmp-editor-alt">
                <GcGridProductSelection
                  :on-item-click="onProductClicked"
                  :on-value-changed="onProductChanged"
                  :ref="'product' + cellInfo.rowIndex"
                  :data-source="Products"
                  :value="cellInfo.value"
                  :cell-info="cellInfo"
                  :disabled="false"
                  :showClearButton="Products.length == 1 ? false : true"
                  :readOnly="Products.length == 1"
                />
              </div>
            </template>

            <template #deliveryAddressEditTemplate="{ data: cellInfo }">
              <div class="gc-auto-cmp-editor">
                <GcGridDeliveryAddressSelection
                  :on-item-click="onDeliveryAddressClicked"
                  :on-value-changed="onDeliveryAddressChanged"
                  :ref="'delivery-address' + cellInfo.rowIndex"
                  :data-source="DeliveryAddresses"
                  :value="cellInfo.value"
                  :cell-info="cellInfo"
                />
              </div>
            </template>

            <template #qtyEditTemplate="{ data }">
              <div>
                <div>
                  <DxNumberBox
                    :value="data.value"
                    @value-changed="salesUMQtyChanged($event, data)"
                    :format="getDecimalFormatByCode(7).Format"
                    class="gc-grid-number-box-editor"
                    :min="0"
                    :max="999999999.99"
                  />
                </div>
              </div>
            </template>
            <template #priceEditTemplate="{ data }">
              <div>
                <div>
                  <DxNumberBox
                    :value="data.value"
                    @value-changed="salesUMFxPriceChanged($event, data)"
                    :format="getDecimalFormatByCode(7).Format"
                    class="gc-grid-number-box-editor"
                    :min="0"
                    :max="999999999.99"
                  />
                </div>
              </div>
            </template>
            <template #sales-um-template="{ data }">
              <div class="gc-auto-cmp-nounderline">{{ data.data.SalesUM }}</div>
            </template>
            <template #date-edit-template="{ data: cellInfo }">
              <div>
                <div>
                  <DxDateBox
                    :value="cellInfo.value"
                    @value-changed="onDateChanged($event, cellInfo)"
                    display-format="dd MMM yyyy"
                    class="gc-grid-number-box-editor"
                  />
                </div>
              </div>
            </template>
            <template #itemSelect="{ data }">
              <div class="custom-auto-complete">
                <span class="auto-complete-primary">
                  {{ data.ProductCode }}
                </span>
                <span class="auto-complete-secondary">
                  {{ data.ProductCode }}
                </span>
              </div>
            </template>
            <template #deliveryAddressTemplate="{ data }">
              <div class="custom-auto-complete data-grid">
                <span class="auto-complete-primary">{{
                  data.CompanyName
                }}</span>
                <div class="auto-complete-secondary no-padding">
                  <span v-if="data.PhysicalAddressStreetLine1 != ''">{{
                    data.PhysicalAddressStreetLine1
                  }}</span>
                  <span v-if="data.PhysicalAddressStreetLine2 != ''">
                    {{ data.PhysicalAddressStreetLine2 }}
                  </span>
                  <span v-if="data.PhysicalAddressSuburb != ''">
                    {{ data.PhysicalAddressSuburb }}
                  </span>
                  <span v-if="data.PhysicalAddressState != ''">
                    {{ data.PhysicalAddressState }}
                  </span>
                  <span v-if="data.PhysicalAddressPostcode != ''">
                    {{ data.PhysicalAddressPostcode }}
                  </span>
                </div>
              </div>
            </template>
          </dx-data-grid>
        </div>
      </DxScrollView>

      <div class="footer-actions">
        <DxButton
          class="action-button"
          text="Generate Orders"
          type="default"
          styling-mode="contained"
          :disabled="isSaving"
          @click="
            () => {
              this.generateOrders();
            }
          "
        />
        <DxButton
          class="action-button"
          text="Back"
          type="normal"
          styling-mode="contained"
          @click="
            (e) => {
              this.hideModal();
            }
          "
        />
      </div>
    </div>
  </DxPopup>
</template>
<script>
import BaseViewMixin from "../v1/_mixins/base.view";
import validationEngine from "devextreme/ui/validation_engine";
import { DxScrollView } from "devextreme-vue/scroll-view";
import { CustomerSalesContractService } from "../_services/api/accounts/customer-sales-contract.service";
import { CustomerFreightChargeService } from "../_services/api/accounts/customer-freight-charge.service";
import { ForeignCurrencyService } from "../_services/api/settings/accounting/foreign-currency.service";
import { CustomerDeliveryAddressService } from "../_services/api/customers/customer-delivery-address.service";
import { SalesOrderService } from "../_services/api/sales/sales-order.service";

import { SalesOrderHeader, SalesOrderDetail } from "../_models/sales-order";
import SalesFormulas from "../_helpers/formulas/sales-formulas";
import { CustomerService } from "../_services/api/customers/customer.service";
import { ProductService } from "../_services/api/products/product.service";

const {
  calculateDiscLineTotal,
  calculateNetLineTotal,
  calculateWetLineTotal,
  calculateTaxableLineTotal,
  calculateTaxLineTotal,
  calculateLineTotal,
  calculateDiscFXLineTotal,
  calculateNetFXLineTotal,
  calculateWetFXLineTotal,
  calculateTaxableFXLineTotal,
  calculateTaxFXLineTotal,
  calculateFXLineTotal,
  calculateCalculatedDiscPcnt,
  calculateSalesUMPriceFromSalesUMFXPrice,
  calculateSalesUMNetPriceFromSalesUMNetFXPrice,
  calculateStockUMNetFXPrice,
  calculateStockUMNetPrice,
  calculateStockUMFXPrice,
  calculateStockUMPrice,
  calculateSalesUMNetFXPriceFromSalesUMFXPrice,
} = SalesFormulas;

var deliveryAddressDataSource = CustomerDeliveryAddressService.dataSourceConfig(
  "api/CustomerDeliveryAddress",
  {
    select: [
      "LineNo",
      "CompanyName",
      "PhysicalAddressStreetLine1",
      "PhysicalAddressStreetLine2",
      "PhysicalAddressSuburb",
      "PhysicalAddressState",
      "PhysicalAddressPostcode",
      "PhysicalAddressCountry",
      "RecordId",
    ],
    key: "RecordId",
  }
);

// eslint-disable-next-line no-unused-vars
let generateSoGridInstance = null;
export default {
  mixins: [BaseViewMixin],
  components: {
    DxScrollView,
  },
  props: {
    showModal: {
      type: Boolean,
      default: () => {
        return false;
      },
    },
    parentModel: {
      type: Object,
      default: () => {},
    },
    parentContractLine: {
      type: Object,
    },
  },
  watch: {
    showModal: {
      immediate: true,
      // eslint-disable-next-line no-unused-vars
      handler: function (newVal, oldVal) {
        if (newVal) {
          this.$logger.trackEventOpenPopup(
            "Generate Sales Order From Contract Line"
          );
        }
      },
    },
  },
  mounted() {
    // eslint-disable-next-line no-console
    this.$logger.debug("parentContractLine", this.parentContractLine);
  },
  data() {
    return {
      isSaving: false,
      salesOrderHeader: new SalesOrderHeader(),
      salesOrderDetailLines: [],
      Products: [],
      ProductPrices: [],
      DeliveryAddresses: [],
      CustomerSalesContractLine: null,
      NextConfirmationNo: 1,
      customerName: "",
      model: {
        AvailableQuantity: 0,
        LineNo: "",
        CustomerCode: "",
        CompanyCode: "",
        BranchCode: " ",
        DivisionCode: "",
        SalesOrderTypeCode: "",
        SalesRepresentativeCode: "",
        NumberOfOrders: 0,
        TotalQuantity: 0,
        FXTotal: 0,
        ExchangeRate: 1,
        CurrencyCode: "",
      },
      freightCharges: [],
    };
  },
  computed: {
    modalTitle() {
      let modalTitle = `Generate Order for Contract #${this.parentModel.Code} Line ${this.parentContractLine.LineNo}`;
      if (
        this.CustomerSalesContractLine &&
        this.CustomerSalesContractLine.CustomerSalesContractItemConsumptions
          .length == 1
      ) {
        modalTitle = `${modalTitle} ${this.CustomerSalesContractLine.CustomerSalesContractItemConsumptions[0].ProductCode}`;
      }

      return modalTitle;
    },
    headerFormItems() {
      let items = [
        {
          itemType: "group",
          caption: "",
          colCount: 3,
          items: [
            {
              itemType: "group",
              caption: "",
              items: [
                {
                  dataField: "NumberOfOrders",
                  label: { text: "# of Sales Orders" },
                  editorType: "dxNumberBox",
                  editorOptions: {
                    readOnly: false,
                    maxLength: 200,
                    onValueChanged: this.onNumberOfOrdersChanged.bind(this),
                  },
                  validationRules: [
                    {
                      type: "required",
                      message: "NumberOfOrders is required",
                    },
                  ],
                },
              ],
            },

            {
              itemType: "group",
              caption: "",
              items: [
                {
                  ref: "TotalQuantity",
                  dataField: "TotalQuantity",
                  label: { text: this.headerFormTotalQuantityLabel },
                  editorType: "dxNumberBox",
                  editorOptions: {
                    readOnly: false,
                    maxLength: 200,
                    onValueChanged: this.onTotalQuantityChanged.bind(this),
                    format: this.getDecimalFormatByCode(2).Format,
                  },
                  validationRules: [
                    {
                      type: "required",
                      message: "TotalQuantity is required",
                    },
                  ],
                },
              ],
            },

            {
              itemType: "group",
              caption: "",
              items: [
                {
                  dataField: "FXTotal",
                  label: { text: this.headerFormFxTotalLabel },
                  editorType: "dxNumberBox",
                  editorOptions: {
                    readOnly: true,
                    maxLength: 200,
                    format: this.getDecimalFormatByCode(12).Format,
                  },
                  validationRules: [
                    {
                      type: "required",
                      message: "Confirmation is required",
                    },
                  ],
                },
              ],
            },
          ],
        },
      ];

      return items;
    },
    generateSoDetailsColumns() {
      return this.parseGridColumns("transaction-editable", [
        {
          gcLabel: "Order No",
          dataField: "OrderNo",
          gcType: "String",
          gcSubType: "PrimaryRecord",
          allowEditing: true,
          allowSorting: false,
          cssClass: this.cellEditorClass,
          showEditorAlways: true,
          editCellTemplate: "textEditTemplate",
        },

        {
          gcLabel: "Agent Ref",
          dataField: "AgentRef",
          gcType: "String",
          width: 250,
          allowEditing: true,
          allowSorting: false,
          showEditorAlways: true,
          editCellTemplate: "textEditTemplate",
        },

        {
          gcLabel: "Confirmation",
          dataField: "Confirmation",
          gcType: "String",
          width: 250,
          allowEditing: true,
          allowSorting: false,
          showEditorAlways: true,
          editCellTemplate: "textEditTemplate",
        },

        {
          gcLabel: "Delivery",
          dataField: "DeliveryAddressCompanyName",
          gcType: "String",
          width: 250,
          showEditorAlways: true,
          editCellTemplate: "deliveryAddressEditTemplate",
          cssClass: "gc-editor-cell",
        },

        {
          gcLabel: "Product",
          dataField: "ItemCode",
          gcType: "String",
          width: 400,
          showEditorAlways: true,
          editCellTemplate: "productEditTemplate",
          cssClass: "gc-editor-cell",
        },

        {
          gcDynamicLabel: this.salesContractColumnQuantityLabel,
          dataField: "SalesUMQty",
          editCellTemplate: "qtyEditTemplate",
          gcType: "Decimal-6-2",
          showEditorAlways: true,
          cssClass: this.cellEditorClass,
        },

        {
          gcDynamicLabel: this.salesContractColumnDefaultPriceLabel,
          dataField: "SalesUMFXPrice",
          editCellTemplate: "priceEditTemplate",
          gcType: "Decimal-6-2",
          showEditorAlways: true,
          cssClass: this.cellEditorClass,
        },

        {
          gcLabel: "Ship Date",
          dataField: "ShipDate",
          gcType: "Date",
          showEditorAlways: true,
          editCellTemplate: "date-edit-template",
          allowEditing: true,
        },

        {
          gcLabel: "Reqd Date",
          dataField: "RequiredByDate",
          gcType: "Date",
          showEditorAlways: true,
          editCellTemplate: "date-edit-template",
          allowEditing: true,
        },
      ]);
    },
    salesContractColumnDefaultPriceLabel() {
      return `Price (${this.parentModel.CurrencyCode})`;
    },
    salesContractColumnQuantityLabel() {
      return `Quantity (${this.parentContractLine.SalesUM})`;
    },

    headerFormFxTotalLabel() {
      return `Total (${this.parentModel.CurrencyCode})`;
    },
    headerFormTotalQuantityLabel() {
      return `Total Quantity (${this.parentContractLine.SalesUM})`;
    },
  },
  methods: {
    onDeliveryAddressChanged(valueChangedEventArg, cellInfo) {
      // eslint-disable-next-line no-console
      this.$logger.debug(valueChangedEventArg, cellInfo);
      let record = cellInfo.data;
      // cellInfo.setValue(valueChangedEventArg.value);
      let newValue = valueChangedEventArg.value;

      record.CustomerDeliveryAddressLineNo = newValue;

      this.setSaleItemPrice(record);
    },
    onProductChanged(valueChangedEventArg, cellInfo) {
      let record = cellInfo.data;
      // cellInfo.setValue(valueChangedEventArg.value);
      let newValue = valueChangedEventArg.value;

      record.ItemCode = newValue;

      if (
        newValue === undefined ||
        newValue == "" ||
        this.ProductPrices.length == 0
      ) {
        return;
      }
      // get new price
      record.DefaultPrice = this.ProductPrices.filter(
        (a) => a.ProductCode == record.ItemCode
      )[0].DefaultPrice;

      record.ProductDescription = this.ProductPrices.filter(
        (a) => a.ProductCode == record.ItemCode
      )[0].ProductDescription;

      record.AvailableQuantity = this.getAvailableQuantity(record.ItemCode);
      if (record.SalesUMQty > record.AvailableQuantity) {
        record.SalesUMQty = record.AvailableQuantity;
      }

      this.setSaleItemPrice(record);
    },
    onDeliveryAddressClicked(e, cellInfo) {
      this.salesOrderDetailLines[cellInfo.rowIndex].DeliveryAddressCompanyName =
        e.itemData.CompanyName;

      this.salesOrderDetailLines[cellInfo.rowIndex].DeliveryAddressStreetLine1 =
        e.itemData.PhysicalAddressStreetLine1;
      this.salesOrderDetailLines[cellInfo.rowIndex].DeliveryAddressStreetLine2 =
        e.itemData.PhysicalAddressStreetLine2;
      this.salesOrderDetailLines[cellInfo.rowIndex].DeliveryAddressSuburb =
        e.itemData.PhysicalAddressSuburb;
      this.salesOrderDetailLines[cellInfo.rowIndex].DeliveryAddressState =
        e.itemData.PhysicalAddressState;
      this.salesOrderDetailLines[cellInfo.rowIndex].DeliveryAddressPostcode =
        e.itemData.PhysicalAddressPostcode;
      this.salesOrderDetailLines[cellInfo.rowIndex].DeliveryAddressCountry =
        e.itemData.PhysicalAddressCountry;
      this.salesOrderDetailLines[
        cellInfo.rowIndex
      ].CustomerDeliveryAddressLineNo = e.itemData.LineNo.toString();
    },
    onProductClicked(e, cellInfo) {
      this.salesOrderDetailLines[cellInfo.rowIndex].ProductDescription =
        e.itemData.Description;
      this.salesOrderDetailLines[cellInfo.rowIndex].ProductCode =
        e.itemData.ProductCode;
    },
    getValidationResult(messages) {
      let validationResult = { brokenRules: [] };
      messages.forEach((m) => {
        validationResult.brokenRules.push({ message: m });
      });

      return validationResult;
    },
    formatNumber(number) {
      return Number(parseFloat(number).toFixed(2)).toLocaleString("en", {
        minimumFractionDigits: 2,
      });
    },
    onTotalQuantityChanged(e) {
      // eslint-disable-next-line no-console
      this.$logger.debug("onTotalQuantityChanged", e);

      // if (e.value > this.model.AvailableQuantity) {
      //   let validationResult = this.getValidationResult([
      //     `${this.parentModel.Name}, Line ${
      //       this.CustomerSalesContractLine.LineNo
      //     } has a remaining contract quantity of ${this.formatNumber(
      //       this.model.AvailableQuantity
      //     )}. The total quantity  ${this.formatNumber(
      //       e.value
      //     )}  exceeds the remaining contract quantity.`,
      //   ]);
      //   this.$emit("show-validation-errors", validationResult);
      //   // this.resetTotalQty(e);
      //   return;
      // }

      // this.model.TotalQuantity = e.previousValue;

      // eslint-disable-next-line no-console
      this.$logger.debug(
        "this.model.TotalQuantity",
        this.model.TotalQuantity,
        e.value
      );
      for (var i = 0; i < this.salesOrderDetailLines.length; i++) {
        var newQty =
          this.model.NumberOfOrders == 0
            ? 0
            : (
                this.model.TotalQuantity / this.model.NumberOfOrders
              ).roundNumber(2);

        this.$logger.debug(
          "Total Quantity Changed:",
          this.salesOrderDetailLines[i]
        );

        if (newQty > this.salesOrderDetailLines[i].AvailableQuantity) {
          let validationResult = this.getValidationResult([
            `${this.parentModel.Name}, product ${
              this.salesOrderDetailLines[i].ItemCode
            }  quantity ${this.formatNumber(
              newQty
            )} exceeds the available quantity ${this.formatNumber(
              this.salesOrderDetailLines[i].AvailableQuantity
            )}.`,
          ]);
          this.$emit("show-validation-errors", validationResult);
          this.resetTotalQty(e);
          return;
        }

        this.salesOrderDetailLines[i].SalesUMQty = newQty;
        this.setSaleItemPrice(this.salesOrderDetailLines[i]);
      }
    },
    resetTotalQty(e) {
      this.$refs["generate-so-header-form"].instance
        .getEditor("TotalQuantity")
        .option("onValueChanged", null);
      this.model.TotalQuantity = e.previousValue;
      setTimeout(() => {
        this.$refs["generate-so-header-form"].instance
          .getEditor("TotalQuantity")
          .option("onValueChanged", this.onTotalQuantityChanged.bind(this));
      }, 1000);
    },
    onNumberOfOrdersChanged(e) {
      var currentLength = this.salesOrderDetailLines.length;
      let newValue = e.value;

      // remove or add detail records and reset qty
      if (newValue > currentLength) {
        for (let i = 0; i < newValue - currentLength; i++) {
          this.addDetails();
        }
      } else if (newValue < currentLength) {
        for (let i = currentLength; i > newValue; i--) {
          this.salesOrderDetailLines.pop();
          this.NextConfirmationNo--;
        }
      }

      for (var i = 0; i < this.salesOrderDetailLines.length; i++) {
        var newQty =
          this.model.NumberOfOrders == 0
            ? 0
            : (
                this.model.TotalQuantity / this.model.NumberOfOrders
              ).roundNumber(2);
        this.salesOrderDetailLines[i].SalesUMQty = newQty;
        this.setSaleItemPrice(this.salesOrderDetailLines[i]);
      }
    },
    async setNextConfirmationNo() {
      let { data } = await CustomerSalesContractService.getLatestConfirmationNo(
        this.model.CustomerCode,
        this.parentModel.Code,
        this.getFactorByCode(650).FactorInt
      );

      if (data.value.length > 0) {
        var no =
          data.value[0][
            "UserComment" + this.getFactorByCode(650).FactorInt
          ].split("-");
        this.NextConfirmationNo = Number(no[1]) + 1;
      }
    },
    calculateTotals() {
      // record is a detail record
      let self = this;

      var includedLines = this.salesOrderDetailLines;

      if (includedLines.length == 0) {
        self.model.FXTotal = 0;
      } else {
        self.model.FXTotal = includedLines.reduce(function (prev, cur) {
          return prev + cur.FXLineTotal + cur.DiscFXLineTotal;
        }, 0);
      }
    },

    onFormContentReady() {
      validationEngine.validateGroup("headerForm");
    },
    generateSoInitialized(e) {
      generateSoGridInstance = e.component;
    },
    async hideModal() {
      let confirmLeaving = await this.$confirm(
        "You have unsaved changes. Are you sure you want to leave?",
        "Unsaved changes"
      );

      if (confirmLeaving) {
        this.reset();
        this.$emit("close-form");
      }
    },

    salesUMFxPriceChanged(e, cellInfo) {
      let record = cellInfo.data;
      record.SalesUMFXPrice = e.value;

      this.recalculateRecordData(record);
    },
    async onOrderNoChanged(e, editorRef) {
      let orderNo = e.value;
      let orderNoUsedPreviously = false;

      if (this.getFactorByCode(578).Factorc !== "Y" || orderNo == "") return;

      if (this.orderNoUsedWithinDetailLines(e.value)) {
        orderNoUsedPreviously = true;
      }

      if (!orderNoUsedPreviously) {
        let result = await SalesOrderService.findOrderNoUsageForSalesOrder(
          e.value,
          "",
          this.parentModel.CustomerCode
        );

        orderNoUsedPreviously = result.data.value.length > 0;
      }

      if (!orderNoUsedPreviously) {
        let result = await SalesOrderService.findOrderNoUsageForInvoice(
          e.value,
          "",
          this.parentModel.CustomerCode
        );
        orderNoUsedPreviously = result.data.value.length > 0;
      }

      if (orderNoUsedPreviously) {
        // Show prompt
        let confirmOrderNoReuse = await this.$confirm(
          `Customer ${this.customerName} has used Customer PO ${orderNo} previously. Will you use Customer PO ${orderNo} again?`,
          "Customer PO Used Previously"
        );

        if (!confirmOrderNoReuse) {
          this.$refs[editorRef].instance.focus();
          this.$refs[editorRef].$el.querySelector("input").select();
        }
      }
    },
    onTextValueChanged(e, cellInfo) {
      let record = cellInfo.data;
      record[cellInfo.column.dataField] = e.value;

      this.$logger.debug(
        "e",
        e,
        "cellInfo",
        cellInfo,
        "Field",
        cellInfo.column.dataField
      );

      if (cellInfo.column.dataField == "OrderNo") {
        let editorRef = "OrderNo-" + cellInfo.rowIndex;
        this.onOrderNoChanged(e, editorRef);
      }
    },
    onDateChanged(e, cellInfo) {
      let record = cellInfo.data;
      // eslint-disable-next-line no-console
      this.$logger.debug("e", e, "cellinfo", cellInfo);
      // this.dataSource[cellInfo.rowIndex].UseByDate = valueChangedArgs.value;
      record[cellInfo.column.dataField] = e.value;

      if (cellInfo.column.dataField == "RequiredByDate") {
        let brokenRules = [];
        if (this.validateRequiredByDate(record) == false) {
          brokenRules.push({
            message: `Required by Date for '${record.OrderNo}' is outside Contract Start and End Date range.`,
          });
          this.$emit("show-validation-errors", { brokenRules: brokenRules });
        }
      }
    },
    salesUMQtyChanged(e, cellInfo) {
      let self = this;
      let record = cellInfo.data;
      record.SalesUMQty = e.value;

      // eslint-disable-next-line no-console
      this.$logger.debug("salesUMQtyChanged record:", record);

      let isQtyValid = true;

      // validate total quantity
      if (this.model.TotalQuantity <= 0) {
        let validationResult = this.getValidationResult([
          "Total Quantity should be greater than 0.",
        ]);
        this.$emit("show-validation-errors", validationResult);

        isQtyValid = false;
      }

      // Assert the record Available Quantity to be greater than Or Equal to entered Qty

      if (e.value > record.AvailableQuantity && isQtyValid) {
        let validationResult = this.getValidationResult([
          `${this.parentModel.Name}, Line ${
            record.CustomerSalesContractLineNo
          } product ${
            record.ProductDescription
          }  Entered quantity ${this.formatNumber(
            e.value
          )} exceeds the available quantity ${this.formatNumber(
            record.AvailableQuantity
          )}.`,
        ]);
        this.$emit("show-validation-errors", validationResult);
        isQtyValid = false;
      }

      var balance = 0;

      if (isQtyValid) {
        var otherContractLines = this.salesOrderDetailLines.filter(
          (item) =>
            !(
              item.ItemCode == record.ItemCode &&
              item.Confirmation == record.Confirmation
            )
        );

        if (otherContractLines.length > 0) {
          balance = otherContractLines.reduce(function (prev, cur) {
            return prev + cur.SalesUMQty;
          }, 0);
        }
      }
      var totalQty = e.value + balance;

      if (totalQty > this.model.TotalQuantity && isQtyValid) {
        let validationResult = this.getValidationResult([
          `Line quantity total ${totalQty} exceeds the Sales Order Total Quantity ${this.model.TotalQuantity}.`,
        ]);
        this.$emit("show-validation-errors", validationResult);
        isQtyValid = false;
      }

      if (totalQty > this.model.AvailableQuantity && isQtyValid) {
        let validationResult = this.getValidationResult([
          `${this.parentModel.Name}, Line ${
            record.CustomerSalesContractLineNo
          } has a remaining contract quantity of ${this.formatNumber(
            this.model.AvailableQuantity
          )}. The quantity for product ${
            record.ProductDescription
          } takes the drawn quantity to ${this.formatNumber(
            totalQty
          )} which exceeds the remaining contract quantity.`,
        ]);
        this.$emit("show-validation-errors", validationResult);
        isQtyValid = false;
      }

      if (!isQtyValid) {
        setTimeout(function () {
          record.SalesUMQty = 0;
          self.recalculateRecordData(record);
        }, 0);
        return;
      }

      this.setSaleItemPrice(record);
    },
    async setSaleItemPrice(record) {
      let self = this;
      await SalesOrderService.setSaleItemPrice(
        record,
        this.model.SalesOrderTypeCode,
        this.model.BranchCode,
        this.model.CustomerCode,
        this.model.ExchangeRate,
        record.RequiredByDate,
        record.RequiredByDate,
        true
      ).then(function () {
        if (self.getFactorByCode(505) !== null) {
          record.IsCommentVisible =
            self.getFactorByCode(505).Factorc.toUpperCase() === "Y";
        }

        if (record.UseMarket.toUpperCase() === "N") {
          record.SalesUMFXPrice = record.DefaultPrice;
        }

        if (record.ApplyFreight == "Y") {
          record.SalesUMFXPrice += self.getFreightCharge(
            record.CustomerDeliveryAddressLineNo
          );
        }

        self.recalculateRecordData(record);
      });
    },
    orderNoUsedWithinDetailLines(orderNo) {
      let record = this.salesOrderDetailLines.filter(
        (x) => x.OrderNo == orderNo
      );
      return record.length > 1;
    },
    validateRequiredByDate(line) {
      let contractStartDate = this.parentModel.StartDate.toSaveDateObject();

      let contractEndDate = this.parentModel.EndDate
        ? this.parentModel.EndDate.toSaveDateObject()
        : null;
      let requiredByDate = line.RequiredByDate.toSaveDate().toSaveDateObject();

      if (contractEndDate) {
        if (
          requiredByDate >= contractStartDate &&
          requiredByDate <= contractEndDate
        ) {
          return true;
        }
      } else {
        if (requiredByDate >= contractStartDate) return true;
      }

      return false;
    },
    validateLines() {
      let brokenRules = [];

      this.salesOrderDetailLines.forEach((line) => {
        if (!(line.OrderNo && line.DeliveryAddressCompanyName)) {
          brokenRules.push({
            message: `Order #, Deliver To are required. Please check if the entered data are valid`,
          });
        }

        // if (this.validateRequiredByDate(line) == false) {
        //   brokenRules.push({
        //     message: `Required by Date for '${line.OrderNo}' is outside Contract Start and End Date range.`,
        //   });
        // }
      });

      if (brokenRules.length > 0) {
        this.$emit("show-validation-errors", { brokenRules: brokenRules });
        return false;
      } else {
        return true;
      }
    },
    generateOrders() {
      this.$logger.trackEventButtonClick(`Contract Generate Order From Line`);

      if (this.validateLines() == false) return;

      // validation prior to save
      let self = this;
      var salesOrders = {};
      salesOrders.Codes = "";
      salesOrders.SalesOrders = [];

      for (var i = 0; i < self.salesOrderDetailLines.length; i++) {
        var salesOrderDetail = self.salesOrderDetailLines[i];
        if (salesOrderDetail.SalesUMQty === 0) {
          continue;
        }

        var salesOrderHeader = new SalesOrderHeader();
        salesOrderHeader.BranchCode = self.model.BranchCode;
        salesOrderHeader.CompanyCode = self.model.CompanyCode;
        salesOrderHeader.CurrencyCode = self.model.CurrencyCode;
        salesOrderHeader.DivisionCode = self.model.DivisionCode;
        salesOrderHeader.SalesOrderTypeCode = self.model.SalesOrderTypeCode;
        salesOrderHeader.SalesRepresentativeCode =
          self.model.SalesRepresentativeCode;

        salesOrderHeader.OrderNo = salesOrderDetail.OrderNo;
        salesOrderHeader.AgentRef = salesOrderDetail.AgentRef;
        salesOrderHeader.Confirmation = salesOrderDetail.Confirmation;
        salesOrderHeader.UserComment1 = "";
        salesOrderHeader.UserComment2 = "";
        salesOrderHeader.UserComment3 = "";
        salesOrderHeader.UserComment4 = "";
        salesOrderHeader.UserComment5 = "";
        salesOrderHeader.UserComment6 = "";
        salesOrderHeader.UserComment7 = "";
        salesOrderHeader.UserComment8 = "";
        salesOrderHeader.UserComment9 = "";
        salesOrderHeader.UserComment10 = "";
        salesOrderHeader.UserComment11 = "";
        salesOrderHeader.UserComment12 = "";
        salesOrderHeader.UserComment13 = "";
        salesOrderHeader.UserComment14 = "";
        salesOrderHeader.UserComment15 = "";
        salesOrderHeader.UserComment16 = "";
        salesOrderHeader.UserComment17 = "";
        salesOrderHeader.UserComment18 = "";
        salesOrderHeader.UserComment19 = "";
        salesOrderHeader.UserComment20 = "";

        if (this.getFactorByCode(649).FactorInt !== 0) {
          salesOrderHeader[
            "UserComment" + this.getFactorByCode(649).FactorInt
          ] = salesOrderHeader.AgentRef;
        }

        if (this.getFactorByCode(650).FactorInt !== 0) {
          salesOrderHeader[
            "UserComment" + this.getFactorByCode(650).FactorInt
          ] = salesOrderHeader.Confirmation;
        }

        salesOrderHeader.ItemCount = Math.floor(salesOrderDetail.StockUMQty);
        salesOrderHeader.ExTaxTotal =
          salesOrderDetail.TaxableLineTotal.roundNumber(2);
        salesOrderHeader.TaxTotal =
          salesOrderDetail.TaxLineTotal.roundNumber(2);

        salesOrderHeader.Total = salesOrderDetail.LineTotal.roundNumber(2);
        salesOrderHeader.FXTotal = salesOrderDetail.FXLineTotal.roundNumber(2);
        salesOrderHeader.Status = "A";
        salesOrderHeader.CustomerCode = self.model.CustomerCode;
        salesOrderHeader.CustomerDeliveryAddressLineNo =
          salesOrderDetail.CustomerDeliveryAddressLineNo;

        salesOrderHeader.DeliveryAddressCompanyName =
          salesOrderDetail.DeliveryAddressCompanyName;
        salesOrderHeader.DeliveryAddressStreetLine1 =
          salesOrderDetail.DeliveryAddressStreetLine1;
        salesOrderHeader.DeliveryAddressStreetLine2 =
          salesOrderDetail.DeliveryAddressStreetLine2;
        salesOrderHeader.DeliveryAddressSuburb =
          salesOrderDetail.DeliveryAddressSuburb;
        salesOrderHeader.DeliveryAddressState =
          salesOrderDetail.DeliveryAddressState;
        salesOrderHeader.DeliveryAddressPostcode =
          salesOrderDetail.DeliveryAddressPostcode;
        salesOrderHeader.DeliveryAddressCountry =
          salesOrderDetail.DeliveryAddressCountry;
        salesOrderHeader.CustomerDeliveryAddressLineNo =
          salesOrderDetail.CustomerDeliveryAddressLineNo;

        salesOrderHeader.ShipDate = salesOrderDetail.ShipDate;
        salesOrderHeader.RequiredByDate = salesOrderDetail.RequiredByDate;
        salesOrderHeader.DateIn = new Date(new Date().toSaveDate());
        salesOrderHeader.ExchangeRate = this.model.ExchangeRate;
        salesOrderHeader.SalesOrderDetails.push(salesOrderDetail);

        salesOrders.SalesOrders.push(salesOrderHeader);
      }

      if (salesOrders.SalesOrders.length == 0) {
        // alert("At least 1 line must have quantity that is not zero.", "Error");
        let validationResult = this.getValidationResult([
          "At least 1 line must have quantity that is not zero.",
        ]);
        this.$emit("show-validation-errors", validationResult);
        return;
      }

      self.saveSalesOrders(salesOrders);
    },
    async saveSalesOrders(salesOrders) {
      this.showLoading(true);

      let self = this;
      let payload = JSON.stringify(salesOrders);
      this.showLoading(true);
      await SalesOrderService.saveSalesOrderMultiple(JSON.parse(payload))
        .then((res) => {
          self.showLoading(false);

          self.$notify(
            `Sales Order(s) ${res.data.Codes} Successfully Saved`,
            "success"
          );

          this.reset();
          this.$emit("close-form", true);
        })
        .catch((error) => {
          this.showLoading(false);
          self.$emit("show-server-errors", error, "Save sales order Failed");
          // self.displayServerError();
          self.refreshAvailableQty();
        });
    },
    async initialize() {
      this.showLoading(true);
      this.loadingCompleted = false;
      this.reset();
      await this.$store.dispatch(
        "screenFactors/getFactors",
        [505, 87, 95, 649, 650, 578]
      );

      let { data } = await CustomerService.getByCode(
        this.parentModel.CustomerCode
      );

      let customer = data.value[0];
      this.customerName = customer.Name;
      this.model.CustomerCode = this.parentModel.CustomerCode;
      this.model.CompanyCode = this.parentModel.CompanyCode;
      this.model.BranchCode = this.parentModel.BranchCode;
      this.model.DivisionCode = this.parentModel.DivisionCode;
      this.model.CurrencyCode = customer.CurrencyCode;
      this.model.SalesRepresentativeCode = customer.SalesRepresentativeCode
        ? customer.SalesRepresentativeCode
        : this.getFactorByCode(95).Factorc;
      this.model.SalesOrderTypeCode = this.getFactorByCode(87).Factorc;

      this.model.AvailableQuantity = this.parentContractLine.AvailableQuantity;

      let currency = await ForeignCurrencyService.getByCode(
        customer.CurrencyCode
      );

      this.model.ExchangeRate = currency.data.value[0].BuyRate;
      this.salesOrderHeader.ExchangeRate = this.model.ExchangeRate;

      this.CustomerSalesContractLine = this.parentContractLine;

      if (this.CustomerSalesContractLine.ApplyFreight == "Y") {
        let ds = CustomerFreightChargeService.dataSourceConfig(
          "api/CustomerSalesContractFreightCharge",
          {
            filter: [
              ["CustomerCode", "=", this.model.CustomerCode],
              "and",
              [
                "UnitOfMeasureCode",
                "=",
                this.CustomerSalesContractLine.SalesUM,
              ],
            ],
          }
        );
        this.freightCharges = await ds.load();
      }
      // eslint-disable-next-line no-console
      // console.log("Freight Charges", this.freightCharges);

      // eslint-disable-next-line no-console
      this.$logger.debug(
        " this.CustomerSalesContractLine",
        this.CustomerSalesContractLine
      );

      await this.updateProductDataSource();
      this.CustomerSalesContractLine.CustomerSalesContractItemConsumptions.forEach(
        (p) => {
          this.ProductPrices.push({
            ProductCode: p.ProductCode,
            DefaultPrice: p.DefaultPrice,
            ProductDescription: p.ProductDescription,
            AvailableQuantity: p.AvailableQuantity,
          });
        }
      );

      deliveryAddressDataSource.filter([
        "CustomerCode",
        "=",
        this.parentModel.CustomerCode,
      ]);

      this.DeliveryAddresses = await deliveryAddressDataSource.load();

      // this.setInitialPrices();
      this.model.NumberOfOrders = 1;
      this.addDetails();
      this.showLoading(false);
      this.loadingCompleted = true;
    },
    getFreightCharge(deliveryCode) {
      let item = this.freightCharges.find(
        (x) => x.DeliveryCode == deliveryCode
      );

      if (item) {
        // eslint-disable-next-line no-console
        // console.log("Freight", item.FreightCharge, salesUM);
        return item.FreightCharge;
      } else return 0;
    },
    async updateProductDataSource() {
      let productFilterItems =
        this.CustomerSalesContractLine.CustomerSalesContractItemConsumptions.map(
          (x) => {
            return ["ItemCode", "=", x.ProductCode];
          }
        );

      let productFilters = [];

      productFilterItems.forEach((pf) => {
        productFilters.push(pf);
        productFilters.push("or");
      });

      productFilters.pop();

      let ds = ProductService.dataSourceConfig("api/SaleItemCompanyBranch", {
        filter: [
          [
            ["CompanyCode", "=", this.model.CompanyCode],
            ["BranchCode", "=", this.model.BranchCode],
            ["ItemType", "=", "S"],
            ["SalesUM", "=", this.CustomerSalesContractLine.SalesUM],
            ["Inactive", "=", "N"],
            ["AvailableForSale", "=", "Y"],
            ["Status", "=", "A"],
          ],
          "and",
          [productFilters],
        ],
        select: [
          "ItemCode",
          "Description",
          "Inactive",
          "SalesUM",
          "BranchCode",
          "CompanyCode",
        ],
      });

      this.Products = await ds.load();

      await this.setNextConfirmationNo();
    },
    setInitialPrices() {
      this.salesOrderDetailLines.forEach((record) => {
        this.setSaleItemPrice(record);
      });
    },
    async refreshAvailableQty() {
      let { data } =
        await CustomerSalesContractService.getCustomerSalesContractLineConsumptionByLineNo(
          this.parentModel.Code,
          this.parentContractLine.LineNo
        );

      this.model.AvailableQuantity = data.value[0].AvailableQuantity;
    },
    getAvailableQuantity(itemCode) {
      return this.ProductPrices.filter((a) => a.ProductCode == itemCode)[0]
        .AvailableQuantity;
    },
    addDetails() {
      let self = this;
      var salesOrderDetail = new SalesOrderDetail();
      salesOrderDetail.Confirmation =
        this.parentModel.Code + "-" + self.NextConfirmationNo;

      // eslint-disable-next-line no-console
      this.$logger.debug("NextConfirmationNo " + self.NextConfirmationNo);

      self.NextConfirmationNo++;

      salesOrderDetail.UseMarket = self.CustomerSalesContractLine.UseMarket;
      salesOrderDetail.ApplyFreight =
        self.CustomerSalesContractLine.ApplyFreight;

      salesOrderDetail.ItemCode = self.Products[0].ItemCode;
      salesOrderDetail.DefaultPrice =
        self.CustomerSalesContractLine.DefaultPrice;

      salesOrderDetail.CustomerSalesContractCode = self.parentModel.Code;
      salesOrderDetail.CustomerSalesContractLineNo =
        self.CustomerSalesContractLine.LineNo;
      salesOrderDetail.ProductDescription = self.Products[0].Description;
      salesOrderDetail.SalesUMQty = 0;

      salesOrderDetail.AvailableQuantity = self.getAvailableQuantity(
        self.Products[0].ItemCode
      );
      salesOrderDetail.SalesUM = self.CustomerSalesContractLine.SalesUM;
      salesOrderDetail.IsCommentVisible = self
        .getFactorByCode(505)
        .Factorc.toUpperCase();

      salesOrderDetail.ShipDate = new Date(new Date().toSaveDate());
      salesOrderDetail.RequiredByDate = new Date(new Date().toSaveDate());

      self.salesOrderDetailLines.push(salesOrderDetail);
    },
    customItemCreating(data) {
      if (!data.text) {
        data.customItem = null;
        return;
      }

      const newItem = {
        CompanyName: data.text,
        PhysicalAddressCountry: "",
        PhysicalAddressPostcode: "",
        PhysicalAddressState: "",
        PhysicalAddressStreetLine1: "",
        PhysicalAddressStreetLine2: "",
        PhysicalAddressSuburb: "",
      };

      data.customItem = newItem;
    },
    resetHeader() {
      this.salesOrderHeader.OrderNo = "";
      this.salesOrderHeader.AgentRef = "";
      this.salesOrderHeader.Confirmation = "";
      this.salesOrderHeader.UserComment1 = "";
      this.salesOrderHeader.UserComment2 = "";
      this.salesOrderHeader.UserComment3 = "";
      this.salesOrderHeader.UserComment4 = "";
      this.salesOrderHeader.UserComment5 = "";
      this.salesOrderHeader.UserComment6 = "";
      this.salesOrderHeader.UserComment7 = "";
      this.salesOrderHeader.UserComment8 = "";
      this.salesOrderHeader.UserComment9 = "";
      this.salesOrderHeader.UserComment10 = "";
      this.salesOrderHeader.UserComment11 = "";
      this.salesOrderHeader.UserComment12 = "";
      this.salesOrderHeader.UserComment13 = "";
      this.salesOrderHeader.UserComment14 = "";
      this.salesOrderHeader.UserComment15 = "";
      this.salesOrderHeader.UserComment16 = "";
      this.salesOrderHeader.UserComment17 = "";
      this.salesOrderHeader.UserComment18 = "";
      this.salesOrderHeader.UserComment19 = "";
      this.salesOrderHeader.UserComment20 = "";
      this.salesOrderHeader.ItemCount = 0;
      this.salesOrderHeader.ExTaxTotal = 0;
      this.salesOrderHeader.TaxTotal = 0;
      this.salesOrderHeader.Total = 0;
      this.salesOrderHeader.Status = "A";
      this.salesOrderHeader.CustomerCode = this.parentModel.CustomerCode;
      this.salesOrderHeader.CustomerDeliveryAddressLineNo = "";
      this.salesOrderHeader.DeliveryAddressCompanyName = "";
      this.salesOrderHeader.DeliveryAddressStreetLine1 = "";
      this.salesOrderHeader.DeliveryAddressStreetLine2 = "";
      this.salesOrderHeader.DeliveryAddressSuburb = "";
      this.salesOrderHeader.DeliveryAddressState = "";
      this.salesOrderHeader.DeliveryAddressPostcode = "";
      this.salesOrderHeader.DeliveryAddressCountry = "";
      this.salesOrderHeader.ShipDate = new Date(new Date().toSaveDate());
      this.salesOrderHeader.RequiredByDate = new Date(new Date().toSaveDate());
      this.salesOrderHeader.DateIn = new Date(new Date().toSaveDate());

      this.salesOrderHeader.ExchangeRate = this.model.ExchangeRate;

      this.model.TotalQuantity = 0;
      this.model.FXTotal = 0;
    },
    showLoading(state) {
      this.isSaving = state;
      this.$emit("show-loading", state);
    },
    reset() {
      this.resetHeader();

      this.NextConfirmationNo = 1;
      this.Products = [];
      this.ProductPrices = [];
      this.salesOrderDetailLines = [];
    },
    recalculateRecordData(record) {
      record.SalesUMNetFXPrice = calculateSalesUMNetFXPriceFromSalesUMFXPrice(
        record.SalesUMFXPrice,
        record.DiscPcnt
      );

      record.SalesUMPrice = calculateSalesUMPriceFromSalesUMFXPrice(
        record.SalesUMFXPrice,
        this.salesOrderHeader.ExchangeRate
      );

      record.CalculatedDiscPcnt = calculateCalculatedDiscPcnt(
        record.SalesUMStandardPrice,
        record.SalesUMPrice
      );

      record.SalesUMNetPrice = calculateSalesUMNetPriceFromSalesUMNetFXPrice(
        record.SalesUMNetFXPrice,
        this.salesOrderHeader.ExchangeRate
      );

      // Stock

      record.StockUMFXPrice = calculateStockUMFXPrice(
        record.SalesUMFXPrice,
        record.SalesUMConversion
      );

      record.StockUMNetFXPrice = calculateStockUMNetFXPrice(
        record.SalesUMNetFXPrice,
        record.SalesUMConversion
      );

      record.StockUMPrice = calculateStockUMPrice(
        record.SalesUMPrice,
        record.SalesUMConversion
      );

      record.StockUMNetPrice = calculateStockUMNetPrice(
        record.SalesUMNetPrice,
        record.SalesUMConversion
      );

      // FX Totals
      record.DiscFXLineTotal = calculateDiscFXLineTotal(
        record.SalesUMFXPrice,
        record.SalesUMNetFXPrice,
        record.SalesUMQty
      );
      record.NetFXLineTotal = calculateNetFXLineTotal(
        record.SalesUMNetFXPrice,
        record.SalesUMQty
      );
      record.WetFXLineTotal = calculateWetFXLineTotal(
        record.NetFXLineTotal,
        record.WetPcnt
      );
      record.TaxableFXLineTotal = calculateTaxableFXLineTotal(
        record.NetFXLineTotal,
        record.WetFXLineTotal
      );
      record.TaxFXLineTotal = calculateTaxFXLineTotal(
        record.NetFXLineTotal,
        record.WetFXLineTotal,
        record.SalesTaxPcnt
      );
      record.FXLineTotal = calculateFXLineTotal(
        record.TaxableFXLineTotal,
        record.TaxFXLineTotal
      );

      // Totals
      record.DiscLineTotal = calculateDiscLineTotal(
        record.DiscFXLineTotal,
        this.salesOrderHeader.ExchangeRate
      );
      record.NetLineTotal = calculateNetLineTotal(
        record.NetFXLineTotal,
        this.salesOrderHeader.ExchangeRate
      );
      record.WetLineTotal = calculateWetLineTotal(
        record.WetFXLineTotal,
        this.salesOrderHeader.ExchangeRate
      );
      record.TaxableLineTotal = calculateTaxableLineTotal(
        record.TaxableFXLineTotal,
        this.salesOrderHeader.ExchangeRate
      );
      record.TaxLineTotal = calculateTaxLineTotal(
        record.TaxFXLineTotal,
        this.salesOrderHeader.ExchangeRate
      );
      record.LineTotal = calculateLineTotal(
        record.FXLineTotal,
        this.salesOrderHeader.ExchangeRate
      );

      this.calculateTotals();
    },
  },
};
</script>
<style lang="scss" scoped>
#popup-wrapper {
  display: flex;
  flex-direction: column;
  position: relative;
  .action-button {
    margin-left: 10px;
  }
  height: 100%;
}
#generate-so-scrollview {
  flex: 1;
  height: calc(100% - 260px);
}
.footer-actions {
  display: flex;
  justify-content: flex-end;
}
</style>
